import { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { devicesActions } from '@/store';
import StaffTelemetryPanel from './StaffTelemetryPanel';
import { getVehicleStatus, formatSpeed, isStationaryFix, STATUS_CONFIG } from '@/lib/utils';
import StatusIndicator from '@/components/shared/StatusIndicator';
import Tooltip from '@/components/shared/Tooltip';
import useStatusThresholds from '@/hooks/useStatusThresholds';
import useVehicleStatuses from '@/hooks/useVehicleStatuses';
import { isPositionStale, staleAgeLabel } from '@/lib/positionFreshness';
import {
  Gauge, Navigation, Power, Zap, Fuel, Locate,
  Car, Thermometer, X, Route, MoreHorizontal, MapPin, User, Share2, History, Info, Copy, Check, RotateCw,
  ChevronRight, ChevronDown, Activity, Clock, DoorOpen, AlertTriangle, Weight,
} from 'lucide-react';
import { buildCanCards } from './canCards';
import { formatCoordinate, COORDINATE_FORMATS } from '@/lib/coordinates';
import DeviceMarkerIcon from '@/components/shared/DeviceMarkerIcon';
import CustomFieldValue from '@/components/shared/CustomFieldValue';
import { getDriverForPosition } from '@/features/drivers/driverUtils';
import useCoordinateFormat from '@/hooks/useCoordinateFormat';
import useUnits, { kmToUnit } from '@/hooks/useUnits';
import useAddressLabel from '@/hooks/useAddressLabel';
import ShareDialog from '@/features/sharing/ShareDialog';
import { customFields as cfApi, devicePhotos as photosApi, storage } from '@/lib/trackizy';
import PhotoLightbox from '@/components/shared/PhotoLightbox';
import { parseSensorConfig, resolveColorInterval, resolveSensorIcon } from '@/lib/sensorUtils';
import { resolveFuelLevel } from '@/features/fuel/fuelLevelResolver';
import useFuelLevels from '@/features/fuel/useFuelLevels';
import { FlaskConical, Settings2 } from 'lucide-react';
import useTelemetryPrefs from '@/hooks/useTelemetryPrefs';
import TelemetryCardPicker from '@/components/shared/TelemetryCardPicker';
import ColdChainCard from '@/features/cold-chain/ColdChainCard';
import { orgTimezone } from '@/features/staff/attendanceModel';
import { useColdChainShared } from '@/features/cold-chain/ColdChainShared';
import useFeatures from '@/hooks/useFeatures';

// Stable card identity. The `key` is the cardOrder/cardVisibility
// pref key — never change a key without a migration. Labels stay in
// the card-build code where they have t() context. Order here is the
// codebase-default order applied when the user hasn't dragged anything.
// `coldChain` (the Cold Chain section) is NOT in this list: it is a full-width section, not a grid
// tile, and exists only for vehicles with cold points. It is prepended to the order when a saved
// cardOrder doesn't know it yet, so existing users see it first. Visibility: missing key = visible.
export const COLD_CHAIN_CARD_KEY = 'coldChain';

/**
 * A picker reorder only knows the cards available for the current selection. Keep every other
 * saved key (e.g. `coldChain` while a vehicle without cold points is selected) at its place:
 * each missing key goes right after the saved key that preceded it.
 */
export function preserveUnavailableKeys(next, saved) {
  const out = [...next];
  saved.forEach((key, i) => {
    if (out.includes(key)) return;
    const prev = saved.slice(0, i).reverse().find((k) => out.includes(k));
    out.splice(prev === undefined ? 0 : out.indexOf(prev) + 1, 0, key);
  });
  return out;
}

export const VEHICLE_CARD_KEYS = [
  'speed', 'heading', 'ignition', 'battery', 'voltage', 'fuel',
  'sats', 'odometer', 'temp', 'driver', 'coordinates',
  'can_rpm', 'can_coolantTemp', 'can_throttle', 'can_engineLoad', 'can_obdSpeed', 'can_obdOdometer', 'can_engineHours',
  'can_fuelLevel', 'can_fuelUsed', 'can_doorOpen', 'can_dtcCount', 'can_axleWeight1', 'can_axleWeight2', 'can_ignitionOnCount',
];

const CAN_ICONS = { Gauge, Thermometer, Activity, Car, Clock, Fuel, DoorOpen, AlertTriangle, Weight, Power };

const frost = (opacity = 0.78) => ({
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
});

const COLOR_STYLES = {
  green:  { bg: 'bg-emerald-500/10 border border-emerald-300/40', text: 'text-emerald-600 dark:text-emerald-400', icon: 'text-emerald-500' },
  yellow: { bg: 'bg-amber-500/10 border border-amber-300/40',   text: 'text-amber-600 dark:text-amber-400',   icon: 'text-amber-500' },
  red:    { bg: 'bg-red-500/10 border border-red-300/40',       text: 'text-red-600 dark:text-red-400',       icon: 'text-red-500' },
};

function TelemetryCard({ icon: Icon, label, value, unit, alert, colorStatus, action }) {
  const cs = colorStatus ? COLOR_STYLES[colorStatus] : null;
  const bgClass = alert
    ? 'bg-red-500/10 border border-red-300/40'
    : cs
      ? cs.bg
      : 'bg-white/40 border border-white/50 hover:bg-white/60';
  const iconClass = alert ? 'text-red-500' : cs ? cs.icon : 'text-slate-400';
  const textClass = alert ? 'text-red-700 dark:text-red-400' : cs ? cs.text : 'text-slate-800 dark:text-slate-200';

  return (
    <div className={`rounded-xl p-2.5 transition-all ${bgClass}`}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={11} className={iconClass} />
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider flex-1">{label}</span>
        {action}
      </div>
      <p className={`text-sm font-bold ${textClass}`}>
        {value}
        {unit && <span className="text-[10px] font-normal text-slate-400 ms-0.5">{unit}</span>}
      </p>
    </div>
  );
}

// COORDS card with local format cycling. Initial format is the user's
// account preference; clicking the cycle button advances through
// Decimal → DMS → MGRS → Plus Code → wrap. The copy button writes the
// currently-displayed text, so cycle-then-copy gets you what you see.
// Local state intentionally does NOT persist back to user.attributes —
// the user's saved preference is the default everywhere else.
const COORD_FORMAT_KEYS = COORDINATE_FORMATS.map((f) => f.value);

function CoordsCard({ icon: Icon, label, lat, lng, defaultFormat }) {
  const initialIdx = Math.max(0, COORD_FORMAT_KEYS.indexOf(defaultFormat || 'decimal'));
  const [formatIdx, setFormatIdx] = useState(initialIdx);
  const [copied, setCopied] = useState(false);
  const currentFormat = COORD_FORMAT_KEYS[formatIdx];
  const value = formatCoordinate(lat, lng, currentFormat);
  const hasCoords = lat != null && lng != null;

  const cycle = (e) => { e.stopPropagation(); setFormatIdx((i) => (i + 1) % COORD_FORMAT_KEYS.length); };
  const copy = async (e) => {
    e.stopPropagation();
    if (!hasCoords || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };

  return (
    <div className="rounded-xl p-2.5 transition-all bg-white/40 border border-white/50 hover:bg-white/60">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon size={11} className="text-slate-400" />
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider flex-1">{label}</span>
        {hasCoords && (
          <>
            <button
              type="button"
              onClick={cycle}
              title={`Cycle format (${currentFormat})`}
              className="p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/40"
            >
              <RotateCw size={10} />
            </button>
            <button
              type="button"
              onClick={copy}
              title="Copy to clipboard"
              className="p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/40"
            >
              {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
            </button>
          </>
        )}
      </div>
      <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={value}>
        {hasCoords ? <bdi>{value}</bdi> : '—'}
      </p>
    </div>
  );
}

// Inline copy button for telemetry cards — shows a check icon for ~1.2s
// after a successful copy so the user gets confirmation.
function CopyCardAction({ text }) {
  const [copied, setCopied] = useState(false);
  const onClick = async (e) => {
    e.stopPropagation();
    if (!text || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/40"
      title="Copy to clipboard"
    >
      {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
    </button>
  );
}

function TelemetryPhotos({ deviceId }) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    if (!deviceId) return;
    photosApi.list(deviceId)
      .then((data) => setPhotos(Array.isArray(data) ? data : []))
      .catch(() => setPhotos([]));
  }, [deviceId]);

  if (photos.length === 0) return null;

  const getPublicUrl = (filePath) => {
    return storage.getPublicUrl(filePath);
  };

  return (
    <>
      <div className="px-3 pb-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider mt-2 mb-1.5">
          {t('settings.photos')}
        </p>
        <div className="flex gap-1.5">
          {photos.map((photo) => (
            <img
              key={photo.id}
              src={getPublicUrl(photo.file_path)}
              alt={photo.file_name}
              className="w-14 h-14 rounded-lg object-cover cursor-pointer border border-white/20 hover:border-white/50 transition-colors"
              onClick={() => setLightboxSrc(getPublicUrl(photo.file_path))}
            />
          ))}
        </div>
      </div>
      {lightboxSrc && (
        <PhotoLightbox src={lightboxSrc} alt="Device photo" onClose={() => setLightboxSrc(null)} />
      )}
    </>
  );
}

// Polymorphic dispatch: staff selection wins. Mutual-exclusion is enforced at
// the dispatch sites (MapView marker clicks + VehicleListPanel row click), so
// both selections truthy at once would be a bug — bias to staff to surface it.
// This wrapper owns ONLY the routing useSelector; each child component owns
// all of its own hooks (otherwise the early return below would skip hooks
// declared after it in the loser branch, violating the Rules of Hooks).
export default function TelemetryPanel(props) {
  const selectedStaffUserId = useSelector((state) => state.staff?.selectedUserId);
  if (selectedStaffUserId) return <StaffTelemetryPanel {...props} />;
  return <VehicleTelemetryPanel {...props} />;
}

function VehicleTelemetryPanel({ replayPosition, onStartReplay, mode = 'floating' }) {
  // 'side' (map-edge drawer) shares the docked card layout but never collapses.
  const dockLike = mode === 'docked' || mode === 'side';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [shareOpen, setShareOpen] = useState(false);
  const prefs = useTelemetryPrefs();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customDefs, setCustomDefs] = useState([]);
  const selectedId = useSelector((state) => state.devices.selectedId);
  const device = useSelector((state) => state.devices.items[selectedId]);
  const position = useSelector((state) => state.session.positions[selectedId]);
  const drivers = useSelector((state) => state.drivers.items);
  const thresholds = useStatusThresholds();
  const units = useUnits();
  const vehicleStatuses = useVehicleStatuses();
  const { temperature: coldModuleOn } = useFeatures();
  const { byDevice: fuelByDevice } = useFuelLevels();
  const coldShared = useColdChainShared();
  const orgTz = orgTimezone(useSelector((s) => s.org?.settings));
  // Enterprise view is read-only and has no org API: no photos, custom fields or sharing.
  const enterpriseMode = useSelector((s) => s.org?.enterprise === true);

  // When replay is active, use replay position data instead of live Redux data
  const displayPosition = replayPosition || position;

  const fmtCoord = useCoordinateFormat();
  // Account-default coordinate format — seeds the CoordsCard's initial cycle
  // index so a freshly-opened panel matches the user's saved preference.
  const userCoordFormat = useSelector((s) => s.session.user?.attributes?.coordinateFormat) || 'decimal';
  const geocodedAddress = useAddressLabel(displayPosition?.latitude, displayPosition?.longitude, displayPosition?.address, 'liveMap');

  useEffect(() => {
    if (enterpriseMode) { setCustomDefs([]); return; }
    cfApi.list().then((defs) => setCustomDefs(Array.isArray(defs) ? defs : [])).catch(() => setCustomDefs([]));
  }, [enterpriseMode]);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      // Clicks inside the picker root are fine — the popover root has
      // stopPropagation already, but the picker uses ColumnManager
      // (absolute-positioned) which renders within data-telemetry-picker-root
      // so the closest() check correctly identifies them.
      if (e.target.closest('[data-telemetry-picker-root]')) return;
      setPickerOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  // Close the picker when the user selects a different vehicle — the
  // picker is panel-scoped, not selection-scoped, but staying open
  // during a switch is disorienting.
  useEffect(() => {
    setPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id]);

  // Auto-expand the docked panel when the user selects a different vehicle.
  useEffect(() => {
    if (mode !== 'docked') return;
    if (device?.id) prefs.setCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id]);

  if (!selectedId || !device) return null;

  // Device icon for header — uses chosen marker icon or falls back to category

  const status = vehicleStatuses.get(device?.id) ?? getVehicleStatus(device, position, thresholds);
  const sc = STATUS_CONFIG[status];
  const attrs = displayPosition?.attributes || {};

  // When the last fix is older than the org's stale threshold, the bottom
  // telemetry strip would otherwise look "live" — showing the last speed /
  // ignition / battery values as if they were current. Surface the age
  // prominently in the header AND mute the live-looking cards so a 36-min-
  // old "17 km/h, ignition on" can't be mistaken for a vehicle that's
  // currently moving. The live-data status badge already says "Offline"
  // when stale; this just makes the dissonance visible. Replay is exempt —
  // we're intentionally showing historical data there.
  const staleMs = (thresholds?.staleMinutes ?? 10) * 60_000;
  const isStale = !replayPosition && isPositionStale(displayPosition, staleMs);
  const staleAge = isStale ? staleAgeLabel(displayPosition, staleMs) : null;

  // "Stale while active" — last fix showed the vehicle moving or ignition-on,
  // but no new fix has arrived. Surfacing common causes (underground parking,
  // weak cellular, SIM/data exhausted, antenna disconnect) helps users answer
  // the natural question "how can ignition be ON if the device is offline?"
  // without having to know the GPS-tracking domain.
  const lastSpeedKmh = displayPosition?.speed ? Number(displayPosition.speed) : 0;
  const lastIgnitionOn = (displayPosition?.attributes || {}).ignition === true;
  const staleWhileActive = isStale && (lastSpeedKmh > 0 || lastIgnitionOn);

  const currentDriver = displayPosition ? getDriverForPosition(displayPosition, drivers) : null;
  // Stationary-fix guard: when the device explicitly reports ignition off
  // AND speed is below the jitter ceiling, the reported speed/heading are
  // GPS chatter on a parked vehicle, not real motion. Render them as 0 /
  // — so the headline numbers agree with the "Stopped" status pill.
  // Replay is exempt — we want the literal historical readings there.
  const isStationary = !replayPosition && isStationaryFix(displayPosition, thresholds);
  // rawSpeedKmh stays km/h — the >90 alert + stale-while-active logic compare
  // against km/h thresholds; only the card VALUE renders in the user's unit.
  const rawSpeedKmh = displayPosition?.speed ? formatSpeed(displayPosition.speed) : 0;
  const speed = isStationary ? 0 : rawSpeedKmh;
  const displaySpeed = isStationary ? 0 : (displayPosition?.speed ? units.formatSpeed(displayPosition.speed) : 0);
  const course = displayPosition?.course || 0;
  const ignition = attrs.ignition ?? false;
  const batteryLevel = attrs.batteryLevel ?? null;
  const voltage = attrs.power ?? attrs.battery ?? null;
  const satellites = attrs.sat ?? attrs.satellites ?? 0;
  const odometer = attrs.totalDistance ?? attrs.odometer ?? null;
  const temp = attrs.deviceTemp ?? attrs.temp ?? null;

  // Fuel: a fuel profile's calibrated litres win, else the legacy device calibration (one shared resolver).
  const fuelLevel = resolveFuelLevel({ overviewRow: fuelByDevice.get(device?.id), device, position: displayPosition });
  const fuel = fuelLevel.value;
  const fuelIsCalibrated = fuelLevel.calibrated;
  const fuelUnitLabel = fuelLevel.unit && fuelLevel.source === 'profile' && fuelLevel.percent != null
    ? <bdi>{`${fuelLevel.unit} · ${Math.round(fuelLevel.percent)}%`}</bdi>
    : fuelLevel.unit;

  // Build cards — only include cards that have meaningful data
  const allCards = [
    { key: 'speed', icon: Gauge, label: t('telemetry.speed'), value: displaySpeed, unit: units.speedLabel, alert: speed > 90, show: true },
    { key: 'heading', icon: Navigation, label: t('telemetry.heading'), value: isStationary ? '—' : `${Math.round(course)}°`, unit: null, show: true },
    { key: 'ignition', icon: Power, label: t('telemetry.ignition'), value: ignition ? t('vehicleList.on') : t('vehicleList.off'), unit: null, show: attrs.ignition !== undefined },
    { key: 'battery', icon: Zap, label: t('telemetry.battery'), value: batteryLevel ?? '—', unit: batteryLevel != null ? '%' : null, alert: batteryLevel != null && batteryLevel < 40, show: batteryLevel != null },
    { key: 'voltage', icon: Zap, label: t('telemetry.voltage'), value: voltage != null ? Number(voltage).toFixed(1) : '—', unit: voltage != null ? 'V' : null, show: voltage != null },
    {
      key: 'fuel',
      icon: Fuel,
      label: (
        <span className="flex items-center gap-1">
          {t('telemetry.fuel')}
          {fuelIsCalibrated && <FlaskConical size={9} className="text-blue-400" />}
        </span>
      ),
      value: fuel != null ? Number(fuel).toFixed(1) : '—',
      unit: fuelUnitLabel,
      alert: fuel != null && !fuelIsCalibrated && fuel < 20,
      show: fuel != null,
    },
    { key: 'sats', icon: Locate, label: t('telemetry.sats'), value: satellites, unit: null, alert: satellites > 0 && satellites < 5, show: true },
    { key: 'odometer', icon: Car, label: t('telemetry.odometer'), value: odometer != null ? `${Math.round(kmToUnit(odometer / 1000, units.distanceUnit))}k` : '—', unit: odometer != null ? units.distanceLabel : null, show: odometer != null },
    { key: 'temp', icon: Thermometer, label: t('telemetry.temp'), value: temp != null ? Number(temp).toFixed(1) : '—', unit: temp != null ? '°C' : null, show: temp != null },
    { key: 'driver', icon: User, label: t('driverBehavior.driver'), value: currentDriver?.name || t('driverBehavior.unknownDriver'), unit: null, show: true },
    // Coordinates card is rendered as a CoordsCard so it can cycle Decimal /
    // DMS / MGRS / Plus Code locally without touching the user's saved
    // preference. The `kind: 'coords'` marker is read by the render loop.
    {
      key: 'coordinates',
      kind: 'coords',
      icon: MapPin,
      label: t('telemetry.coordinates'),
      lat: displayPosition?.latitude,
      lng: displayPosition?.longitude,
      show: true,
    },
  ];
  // Custom sensor cards from device sensorConfig
  const sensorConfig = parseSensorConfig(device?.attributes?.sensorConfig);
  const customCards = sensorConfig.map((sensor) => {
    const rawValue = attrs?.[sensor.attribute] ?? null;
    const numValue = rawValue != null ? Number(rawValue) : null;
    const isInvalid = numValue != null && sensor.validation && (
      (sensor.validation.min != null && numValue < sensor.validation.min) ||
      (sensor.validation.max != null && numValue > sensor.validation.max)
    );
    const colorStatus = isInvalid ? null : resolveColorInterval(numValue, sensor.intervals);
    const IconComponent = resolveSensorIcon(sensor.icon);
    return {
      key: `sensor_${sensor.attribute}`,
      icon: IconComponent,
      label: sensor.name,
      value: rawValue != null ? String(rawValue) : '—',
      unit: sensor.unit || null,
      alert: isInvalid,
      colorStatus: isInvalid ? null : colorStatus,
      show: true,
    };
  });

  const canCards = buildCanCards(device, attrs, t, CAN_ICONS);
  const cardsRaw = [...allCards.filter((c) => c.show), ...customCards, ...canCards];

  // Apply user pref: keep only visible cards (missing-key = visible),
  // reorder to match cardOrder (unknown new cards go to the end so the
  // pref is forward-compatible).
  const cards = (() => {
    const filtered = cardsRaw.filter((c) => prefs.isCardVisible(c.key));
    if (prefs.cardOrder.length === 0) return filtered;
    const byKey = new Map(filtered.map((c) => [c.key, c]));
    const ordered = [];
    prefs.cardOrder.forEach((k) => { if (byKey.has(k)) { ordered.push(byKey.get(k)); byKey.delete(k); } });
    byKey.forEach((c) => ordered.push(c));
    return ordered;
  })();

  // Cold Chain section: offered only when the org has the module and this vehicle has cold points.
  const coldAvailable = !!coldModuleOn && (coldShared?.byDevice.get(selectedId)?.length ?? 0) > 0;
  const coldOrder = coldAvailable && !prefs.cardOrder.includes(COLD_CHAIN_CARD_KEY)
    ? [COLD_CHAIN_CARD_KEY, ...prefs.cardOrder]
    : prefs.cardOrder;
  const coldVisible = coldAvailable && prefs.isCardVisible(COLD_CHAIN_CARD_KEY);
  // Above the tiles unless the user dragged a visible tile in front of it.
  const coldIdx = coldOrder.indexOf(COLD_CHAIN_CARD_KEY);
  const coldTop = coldIdx < 0 || !coldOrder.slice(0, coldIdx).some((k) => cards.some((c) => c.key === k));
  const coldCard = coldVisible ? <ColdChainCard deviceId={selectedId} className="mx-3 my-3" variant={mode === 'floating' ? 'compact' : 'full'} tz={orgTz} /> : null;

  // What the picker shows. Available = every card whose data the
  // current selection has (cardsRaw before visibility filter).
  const availableCards = [
    ...(coldAvailable ? [{ id: COLD_CHAIN_CARD_KEY, label: t('telemetry.coldChain', 'Cold chain') }] : []),
    ...cardsRaw.map((c) => ({
      id: c.key,
      // Picker shows plain string labels; coerce JSX labels (the Fuel
      // card label is a fragment) to a sensible string fallback.
      label: typeof c.label === 'string' ? c.label : (c.key.charAt(0).toUpperCase() + c.key.slice(1)),
    })),
  ];

  const close = () => dispatch(devicesActions.selectId(null));

  // Shared card renderer — used by both floating and docked branches.
  // CoordsCard needs userCoordFormat from scope; all other cards use TelemetryCard.
  const renderCard = (card) => {
    if (card.kind === 'coords') {
      return <CoordsCard key={card.key} icon={card.icon} label={card.label} lat={card.lat} lng={card.lng} defaultFormat={userCoordFormat} />;
    }
    return <TelemetryCard key={card.key} {...card} />;
  };

  if (dockLike) {
    return (
      <DockedTelemetryRender
        collapsible={mode === 'docked'}
        prefs={prefs}
        cards={cards}
        availableCards={availableCards}
        device={device}
        status={status}
        staleAge={staleAge}
        staleWhileActive={staleWhileActive}
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
        close={close}
        renderCard={renderCard}
        coldCard={coldCard}
        coldTop={coldTop}
        coldOrder={coldOrder}
        t={t}
      />
    );
  }

  return (
    <div
      style={{
        ...frost(0.78),
        borderTop: '1px solid var(--glass-border)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.06)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <StatusIndicator status={status} size={14} />
          <DeviceMarkerIcon device={device} size={18} color={sc.color} />
          <div>
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200"><bdi>{device.name}</bdi></h4>
            <p className="text-[10px] text-slate-500">
              {geocodedAddress || t('vehicleList.noAddress')}
            </p>
          </div>
          <span
            className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white ms-1"
            style={{ backgroundColor: sc.color, boxShadow: `0 2px 6px ${sc.color}40` }}
          >
            {t(sc.label)}
          </span>
          {isStale && staleAge && (
            <span
              className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 ms-1.5"
              title={t('telemetry.staleHint', 'Values shown are from the last fix — not live.')}
            >
              {t('telemetry.lastFix', 'Last fix')} {staleAge}
            </span>
          )}
          {staleWhileActive && (
            // Compact inline explainer for the "device went offline mid-drive"
            // case. Renders as a short "Last seen moving" pill; the four likely
            // causes live in the hover tooltip so the header stays single-line
            // even on narrow panels.
            <Tooltip
              content={
                <div className="space-y-0.5">
                  <div className="font-semibold mb-1">
                    {t('telemetry.staleActiveTitle', 'Last seen moving — likely causes:')}
                  </div>
                  <div>• {t('telemetry.staleActiveCause1', 'underground parking')}</div>
                  <div>• {t('telemetry.staleActiveCause2', 'weak cellular signal')}</div>
                  <div>• {t('telemetry.staleActiveCause3', 'SIM/data exhausted')}</div>
                  <div>• {t('telemetry.staleActiveCause4', 'antenna disconnected')}</div>
                </div>
              }
            >
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 ms-1.5 cursor-help">
                <Info size={11} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />
                <span className="font-medium">
                  {t('telemetry.staleActiveCompact', 'Last seen moving')}
                </span>
              </span>
            </Tooltip>
          )}
          {replayPosition && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700 ms-1.5">
              {t('miniReplay.replayRoute')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onStartReplay && (
            <button
              type="button"
              onClick={(e) => onStartReplay(selectedId, device.name, e.currentTarget)}
              className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 hover:text-slate-600 transition-colors"
              title={t('miniReplay.replayRoute')}
            >
              <History size={14} />
            </button>
          )}
          {!enterpriseMode && (
            <button onClick={() => setShareOpen(true)} className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 transition-all" title={t('sharing.share')}>
              <Share2 size={14} />
            </button>
          )}
          <button className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 transition-all">
            <Route size={14} />
          </button>
          <button className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 transition-all">
            <MoreHorizontal size={14} />
          </button>
          <div className="relative" data-telemetry-picker-root>
            <Tooltip content={t('telemetry.displaySettings', 'Telemetry display')}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPickerOpen((p) => !p); }}
                className={`p-1.5 rounded-lg transition-all ${
                  pickerOpen
                    ? 'bg-blue-500/15 text-blue-600'
                    : 'text-slate-400 hover:bg-white/40 hover:text-slate-600'
                }`}
                aria-label={t('telemetry.displaySettings', 'Telemetry display')}
                aria-expanded={pickerOpen}
                aria-haspopup="true"
              >
                <Settings2 size={14} />
              </button>
            </Tooltip>
            {pickerOpen && (
              // Floating mode: panel is anchored to the bottom of the
              // viewport, so the popover opens UPWARD to avoid being
              // clipped off the page edge.
              <div className="absolute bottom-full mb-2 end-0 z-50">
                <TelemetryCardPicker
                  availableCards={availableCards}
                  layout={prefs.layout}
                  setLayout={prefs.setLayout}
                  visibleCards={prefs.visibleCards}
                  isCardVisible={prefs.isCardVisible}
                  cardOrder={coldOrder}
                  setVisibility={prefs.setVisibility}
                  setCardOrder={(next) => prefs.setCardOrder(preserveUnavailableKeys(next, prefs.cardOrder))}
                  onReset={prefs.reset}
                  onClose={() => setPickerOpen(false)}
                  t={t}
                />
              </div>
            )}
          </div>
          <button onClick={close} className="p-1.5 rounded-lg hover:bg-white/40 text-slate-400 transition-all">
            <X size={14} />
          </button>
        </div>
      </div>

      {coldTop && coldCard}

      {/* Telemetry cards grid — muted when the data is stale so a 36-min-old
          speedometer reading doesn't render with full opacity. */}
      <div
        className={`p-3 grid gap-1.5 ${cards.length <= 6 ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6' : 'grid-cols-4 sm:grid-cols-5 lg:grid-cols-9'} ${isStale ? 'opacity-50' : ''}`}
        title={isStale ? t('telemetry.staleHint', 'Values shown are from the last fix — not live.') : undefined}
      >
        {cards.map((card, i) => (
          card.kind === 'coords'
            ? <CoordsCard key={i} icon={card.icon} label={card.label} lat={card.lat} lng={card.lng} defaultFormat={userCoordFormat} />
            : <TelemetryCard key={i} {...card} />
        ))}
      </div>

      {!coldTop && coldCard}

      {!enterpriseMode && <TelemetryPhotos deviceId={selectedId} />}

      {!enterpriseMode && customDefs.length > 0 && device && (
        <section className="mt-4 pt-3 px-3 pb-3 border-t border-slate-100 dark:border-slate-800">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            {t('customFields.detailsHeading', 'Details')}
          </h4>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
            {customDefs
              .slice()
              .sort((a, b) => a.display_order - b.display_order)
              .map((def) => (
                <Fragment key={def.id}>
                  <dt className="text-slate-500">{def.label}</dt>
                  <dd className="text-slate-800 dark:text-slate-200">
                    <CustomFieldValue definition={def} value={(device.custom_fields || {})[def.key]} />
                  </dd>
                </Fragment>
              ))}
          </dl>
        </section>
      )}

      {shareOpen && !enterpriseMode && (
        <ShareDialog
          deviceId={selectedId}
          deviceName={device.name}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

function DockedTelemetryRender({
  collapsible = true,
  prefs,
  cards,
  availableCards,
  device,
  status,
  staleAge,
  staleWhileActive,
  pickerOpen,
  setPickerOpen,
  close,
  renderCard,
  coldCard,
  coldTop,
  coldOrder,
  t,
}) {
  const collapsed = collapsible && prefs.collapsed;
  const setCollapsed = prefs.setCollapsed;

  const title = (
    <>
      <StatusIndicator status={status} size={12} />
      <span className="text-xs font-semibold truncate text-slate-700 dark:text-slate-200">
        <bdi>{device?.name ?? '—'}</bdi>
      </span>
    </>
  );

  return (
    <div className="px-3 py-2.5">
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-2 flex-1 min-w-0 text-start"
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            {title}
            {collapsed && (
              <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">
                {t('telemetry.cardCount', '{{n}} cards', { n: cards.length })}
              </span>
            )}
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">{title}</div>
        )}
        {!collapsed && staleAge && (
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700 flex-shrink-0">
            {t('telemetry.lastFix', 'Last fix')} {staleAge}
          </span>
        )}
        {!collapsed && staleWhileActive && (
          <Tooltip
            content={
              <div className="space-y-0.5">
                <div className="font-semibold mb-1">
                  {t('telemetry.staleActiveTitle', 'Last seen moving — likely causes:')}
                </div>
                <div>• {t('telemetry.staleActiveCause1', 'underground parking')}</div>
                <div>• {t('telemetry.staleActiveCause2', 'weak cellular signal')}</div>
                <div>• {t('telemetry.staleActiveCause3', 'SIM/data exhausted')}</div>
                <div>• {t('telemetry.staleActiveCause4', 'antenna disconnected')}</div>
              </div>
            }
          >
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-slate-100 text-slate-600 flex-shrink-0 cursor-help">
              <Info size={9} />
              {t('telemetry.staleActiveCompact', 'Last seen moving')}
            </span>
          </Tooltip>
        )}
        <div className="relative flex-shrink-0" data-telemetry-picker-root>
          <Tooltip content={t('telemetry.displaySettings', 'Telemetry display')}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPickerOpen((p) => !p); }}
              className={`p-1 rounded-md transition-all ${
                pickerOpen ? 'bg-blue-500/15 text-blue-600' : 'text-slate-400 hover:bg-white/40'
              }`}
              aria-label={t('telemetry.displaySettings', 'Telemetry display')}
              aria-expanded={pickerOpen}
              aria-haspopup="true"
            >
              <Settings2 size={12} />
            </button>
          </Tooltip>
          {pickerOpen && (
            <div className="absolute top-full mt-2 end-0 z-50">
              <TelemetryCardPicker
                availableCards={availableCards}
                layout={prefs.layout}
                setLayout={prefs.setLayout}
                visibleCards={prefs.visibleCards}
                isCardVisible={prefs.isCardVisible}
                cardOrder={coldOrder}
                setVisibility={prefs.setVisibility}
                setCardOrder={(next) => prefs.setCardOrder(preserveUnavailableKeys(next, prefs.cardOrder))}
                onReset={prefs.reset}
                onClose={() => setPickerOpen(false)}
                t={t}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          className="p-1 rounded-md text-slate-400 hover:bg-white/40 hover:text-slate-600 flex-shrink-0"
          aria-label={t('common.close', 'Close')}
        >
          <X size={12} />
        </button>
      </div>

      {/* Body: cards grid, only when expanded */}
      {!collapsed && (
        <>
          {coldTop && coldCard && <div className="-mx-3 -mt-1">{coldCard}</div>}
          <div className="grid grid-cols-2 gap-2">
            {cards.map((card) => (
              <div key={card.key} className={card.kind === 'coords' ? 'col-span-2' : ''}>
                {renderCard(card)}
              </div>
            ))}
          </div>
          {!coldTop && coldCard && <div className="-mx-3 mt-1">{coldCard}</div>}
        </>
      )}
    </div>
  );
}
